import { getManifestUrl } from '@xing-com/crate-core-assets';
import {
  createRouteMatcher,
  type RouteMatcher,
} from '@xing-com/crate-core-router';
import { extractMetadataMap } from '@xing-com/crate-runtime';
import type { ErrorReporter } from '@xing-com/crate-xinglet';
import { combineErrorReporters } from '@xing-com/crate-xinglet';
import type { GlobalScope } from '@xing-com/crate-xinglet/internal';

import { createBrowserRuntime } from '../create-browser-runtime';
import { setupDash0 } from '../dash0';
import { setupDataDog } from '../datadog';
import { hasCookieFlag } from '../has-cookie-flag';
import { loadManifestMap } from '../load-manifest-map';
import { render } from '../render';

declare const globalThis: GlobalScope;

(async () => {
  let reportError: ErrorReporter = (
    error,
    { componentStack, ...context } = {}
  ) => {
    if (error instanceof Error) {
      if (componentStack) {
        console.error(error.message, componentStack);
      } else {
        console.error(error.message);
      }
    } else {
      console.error(error);
    }

    if (Object.values(context).filter(Boolean).length) {
      console.error('The above error provided this context', context);
    }
  };

  try {
    const bootstrapConfig = globalThis.crate;
    const {
      dataDog,
      dash0Config,
      serverData = {},
      startXinglets = [],
      ...config
    } = bootstrapConfig;

    const matchRouteReference: { matchRoute?: RouteMatcher } = {};

    reportError = combineErrorReporters(
      reportError,
      setupDataDog(matchRouteReference, dataDog),
      setupDash0(dash0Config)
    );

    const manifestUrl = getManifestUrl(bootstrapConfig);

    let manifestMap;
    try {
      manifestMap = await loadManifestMap(manifestUrl);
    } catch (cause) {
      throw new Error(`Failed to load manifestMap "${manifestUrl}"`, { cause });
    }
    const metadataMap = extractMetadataMap(manifestMap);
    const matchRoute = createRouteMatcher(metadataMap, config.entryPoint);
    matchRouteReference.matchRoute = matchRoute;
    const runtime = createBrowserRuntime({
      config,
      manifestMap,
      matchRoute,
      metadataMap,
      reportError,
      serverData,
    });

    if (LOCAL_DEVELOPMENT || hasCookieFlag('report-error')) {
      const { reportErrorToScreen } = await import('../dev-mode');

      reportError = combineErrorReporters(reportError, reportErrorToScreen);
    }

    await render(runtime, {
      reportError,
      startXinglets,
    });
  } catch (error) {
    reportError(error);
    throw error;
  }
})().catch((error) => {
  console.error(error);
  const pre = document.createElement('pre');
  const text = document.createTextNode(error.stack ?? error);
  pre.appendChild(text);
  document.getElementById('app')?.replaceWith(pre);
});
